// src/services/authService.js - UPDATED WITH FORGOT/RESET PASSWORD
import { API_BASE_URL } from '../utils/constants';


const API_URL = `${API_BASE_URL}/users`;

// ========================================
// SECURITY VALIDATION CLASS
// ========================================
class SecurityValidator {
  // SQL injection patterns
  static SQL_PATTERNS = [
    /\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|SCRIPT)\b/i,
    /--/,        // SQL comment
    /\/\*/,      // SQL comment start
    /\*\//,      // SQL comment end
    /;/,         // SQL statement terminator
    /'/,         // Single quote
    /"/,         // Double quote
    /xp_/i,      // Extended stored procedures
    /sp_/i,      // Stored procedures
  ];

  // XSS patterns
  static XSS_PATTERNS = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onclick=/i,
    /onload=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];

  /**
   * Check if input contains SQL injection patterns
   */
  static containsSQLInjection(value) {
    if (typeof value !== 'string') return false;
    
    return this.SQL_PATTERNS.some(pattern => pattern.test(value));
  }

  /**
   * Check if input contains XSS patterns
   */
  static containsXSS(value) {
    if (typeof value !== 'string') return false;
    
    return this.XSS_PATTERNS.some(pattern => pattern.test(value));
  }

  /**
   * Validate name fields (surname, first_name, middle_name)
   * Rules:
   * - Letters only (a-z, A-Z)
   * - No numbers
   * - No spaces (each name in separate field)
   * - No hyphens
   * - No special characters
   */
  static validateNameField(name, fieldName = 'Name') {
    if (!name) {
      throw new Error(`${fieldName} is required`);
    }

    // Check for SQL injection
    if (this.containsSQLInjection(name)) {
      throw new Error(`Invalid ${fieldName} format. SQL queries not allowed.`);
    }

    // Check for XSS
    if (this.containsXSS(name)) {
      throw new Error(`Invalid ${fieldName} format. HTML/Script tags not allowed.`);
    }

    // Check letters only (no numbers, spaces, hyphens, special chars)
    if (!/^[a-zA-Z]+$/.test(name)) {
      throw new Error(`${fieldName} must contain only letters (no numbers, spaces, or special characters)`);
    }

    return name.trim();
  }

  /**
   * Validate password strength
   * - Must be at least 8 characters
   * - Must contain letters
   * - Must contain at least one number
   * - Must contain at least one special character
   * - No SQL injection
   */
  static validatePassword(password) {
    if (!password) {
      throw new Error('Password is required');
    }

    // Check for SQL injection
    if (this.containsSQLInjection(password)) {
      throw new Error('Invalid password format. SQL queries not allowed.');
    }

    // Check minimum length
    if (password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }

    // Check for at least one letter
    if (!/[a-zA-Z]/.test(password)) {
      throw new Error('Password must contain at least one letter');
    }

    // Check for at least one number
    if (!/\d/.test(password)) {
      throw new Error('Password must contain at least one number');
    }

    // Check for at least one special character
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password)) {
      throw new Error('Password must contain at least one special character (!@#$%^&*, etc.)');
    }

    return password;
  }

  /**
   * Validate email format
   */
  static validateEmail(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    // Check for SQL injection
    if (this.containsSQLInjection(email)) {
      throw new Error('Invalid email format. SQL queries not allowed.');
    }

    // Check for XSS
    if (this.containsXSS(email)) {
      throw new Error('Invalid email format. HTML/Script tags not allowed.');
    }

    // Basic email format validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    return email.toLowerCase();
  }

  /**
   * Validate phone number
   */
  static validatePhoneNumber(phoneNumber) {
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    // Check for SQL injection
    if (this.containsSQLInjection(phoneNumber)) {
      throw new Error('Invalid phone number format');
    }

    // Remove spaces and dashes
    const cleaned = phoneNumber.replace(/[\s\-]/g, '');

    // Check if it contains only numbers and optional + sign
    if (!/^\+?\d+$/.test(cleaned)) {
      throw new Error('Phone number must contain only numbers and optional + sign');
    }

    return phoneNumber;
  }

  /**
   * Validate referral code
   */
  static validateReferralCode(code) {
    if (!code) return null;

    // Check for SQL injection
    if (this.containsSQLInjection(code)) {
      throw new Error('Invalid referral code format');
    }

    // Check for XSS
    if (this.containsXSS(code)) {
      throw new Error('Invalid referral code format');
    }

    return code;
  }
}

// ========================================
// HELPER FUNCTIONS
// ========================================

// Helper function to get consistent auth headers
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

// Helper function to get referral code from multiple sources
const getReferralCode = (formData) => {
  // 1. Check if already in form data
  if (formData.referral_code) {
    console.log('📝 Referral code from form data:', formData.referral_code);
    return formData.referral_code;
  }
  
  // 2. Check current URL for ref parameter
  const urlParams = new URLSearchParams(window.location.search);
  const refFromUrl = urlParams.get('ref');
  if (refFromUrl) {
    console.log('🔗 Referral code from current URL:', refFromUrl);
    return refFromUrl;
  }
  
  // 3. Check localStorage (as backup)
  const refFromStorage = localStorage.getItem('pending_referral_code');
  if (refFromStorage) {
    console.log('💾 Referral code from localStorage:', refFromStorage);
    return refFromStorage;
  }
  
  console.log('⚠️ No referral code found');
  return null;
};

// ========================================
// AUTH SERVICE
// ========================================

export const authService = {
  /**
   * Register new user with comprehensive validation
   */
  async register(userData) {
    try {
      // ✅ STEP 1: Validate all inputs BEFORE sending to backend
      const validatedSurname = SecurityValidator.validateNameField(userData.surname, 'Surname');
      const validatedFirstName = SecurityValidator.validateNameField(userData.first_name, 'First name');
      const validatedMiddleName = SecurityValidator.validateNameField(userData.middle_name, 'Middle name');
      const validatedEmail = SecurityValidator.validateEmail(userData.email);
      const validatedPassword = SecurityValidator.validatePassword(userData.password);
      const validatedPhone = SecurityValidator.validatePhoneNumber(userData.phone_number);
      
      // Get and validate referral code
      const referralCode = getReferralCode(userData);
      const validatedReferralCode = referralCode ? SecurityValidator.validateReferralCode(referralCode) : null;

      // ✅ STEP 2: Prepare sanitized data
      const sanitizedData = {
        surname: validatedSurname,
        first_name: validatedFirstName,
        middle_name: validatedMiddleName,
        email: validatedEmail,
        password: validatedPassword,
        phone_number: validatedPhone
      };

      // Add referral code if present
      if (validatedReferralCode) {
        sanitizedData.referral_code = validatedReferralCode;
        console.log('✅ Final referral code being sent:', validatedReferralCode);
      }

      console.log('📤 Registration data being sent:', {
        ...sanitizedData,
        password: '***hidden***'
      });

      // ✅ STEP 3: Send to backend
      const response = await fetch(`${API_URL}/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedData),
      });

      // ✅ STEP 4: Handle response
      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Registration failed:', error);
        
        // Extract meaningful error message
        if (error.surname) {
          throw new Error(error.surname[0] || error.surname);
        }
        if (error.first_name) {
          throw new Error(error.first_name[0] || error.first_name);
        }
        if (error.middle_name) {
          throw new Error(error.middle_name[0] || error.middle_name);
        }
        if (error.email) {
          throw new Error(error.email[0] || error.email);
        }
        if (error.password) {
          throw new Error(error.password[0] || error.password);
        }
        if (error.phone_number) {
          throw new Error(error.phone_number[0] || error.phone_number);
        }
        
        throw new Error(error.message || error.error || 'Registration failed');
      }

      const result = await response.json();
      console.log('✅ Registration response received');

      // Clear stored referral code after successful registration
      localStorage.removeItem('pending_referral_code');

      return result;

    } catch (error) {
      console.error('❌ Registration validation error:', error.message);
      throw error;
    }
  },

  /**
   * Login user with comprehensive validation
   */
  async login(credentials) {
    try {
      // ✅ STEP 1: Validate inputs BEFORE sending to backend
      const validatedEmail = SecurityValidator.validateEmail(credentials.email);
      
      // Password validation for login (check format, not strength)
      if (!credentials.password) {
        throw new Error('Password is required');
      }
      if (SecurityValidator.containsSQLInjection(credentials.password)) {
        throw new Error('Invalid password format');
      }

      // ✅ STEP 2: Prepare sanitized credentials
      const sanitizedCredentials = {
        email: validatedEmail,
        password: credentials.password
      };

      console.log('🔐 Login attempt for:', validatedEmail);

      // ✅ STEP 3: Send to backend
      const response = await fetch(`${API_URL}/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(sanitizedCredentials),
      });

      // ✅ STEP 4: Handle response
      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Login failed:', error);
        
        // Handle specific error cases
        // Handle frozen account - show reason to user
        if (response.status === 403 && error.is_frozen) {
          const msg = error.reason && error.reason !== 'No reason provided'
            ? `Your account has been frozen. Reason: ${error.reason}`
            : 'Your account has been frozen. Please contact support.';
          throw new Error(msg);
        }

        if (error.email_verified === false) {
          throw new Error(error.error || 'Email not verified');
        }
        
        throw new Error(error.error || error.message || 'Login failed');
      }

      const data = await response.json();

      // ✅ STEP 5: User data stored in React Context state (secure)
      // No localStorage needed - removed for security
      // AuthContext fetches fresh data from API on page load

      return data;

    } catch (error) {
      console.error('❌ Login validation error:', error.message);
      throw error;
    }
  },

  /**
   * Logout user
   */
  async logout() {
    try {
      const response = await fetch(`${API_URL}/logout/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.warn('⚠️ Logout API call failed, but clearing local data anyway');
      }
    } catch (error) {
      console.warn('⚠️ Logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('user');
      console.log('✅ Logged out successfully');
    }
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token) {
    const response = await fetch(`${API_URL}/verify-email/${token}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Email verification failed');
    }
    
    return await response.json();
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(email) {
    const response = await fetch(`${API_URL}/resend-verification/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to resend verification email');
    }
    
    return await response.json();
  },

  /**
   * Request a password reset email.
   * Backend always returns 200 regardless of whether the email exists
   * (prevents email enumeration attacks).
   */
  async forgotPassword(email) {
    const trimmedEmail = email.trim().toLowerCase();

    // Client-side format check
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      throw new Error('Please enter a valid email address.');
    }

    // Security checks
    if (SecurityValidator.containsSQLInjection(trimmedEmail)) {
      throw new Error('Invalid email format.');
    }
    if (SecurityValidator.containsXSS(trimmedEmail)) {
      throw new Error('Invalid email format.');
    }

    console.log('🔑 Requesting password reset for:', trimmedEmail);

    const response = await fetch(`${API_URL}/forgot-password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email: trimmedEmail }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Something went wrong. Please try again.');
    }

    const data = await response.json();
    console.log('✅ Password reset email requested successfully');
    return data;
  },

  /**
   * Submit new password using the reset token from the URL.
   */
  async resetPassword(token, newPassword, confirmPassword) {
    if (!newPassword || !confirmPassword) {
      throw new Error('Both password fields are required.');
    }

    if (newPassword !== confirmPassword) {
      throw new Error('Passwords do not match.');
    }

    // Client-side strength validation (mirrors backend rules)
    if (newPassword.length < 8) {
      throw new Error('Password must be at least 8 characters long.');
    }
    if (!/[a-zA-Z]/.test(newPassword)) {
      throw new Error('Password must contain at least one letter.');
    }
    if (!/\d/.test(newPassword)) {
      throw new Error('Password must contain at least one number.');
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(newPassword)) {
      throw new Error('Password must contain at least one special character (!@#$%^&*, etc.).');
    }

    // Security check on password
    if (SecurityValidator.containsSQLInjection(newPassword)) {
      throw new Error('Invalid password format.');
    }

    console.log('🔒 Submitting new password for token:', token);

    const response = await fetch(`${API_URL}/reset-password/${token}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('❌ Password reset failed:', error);
      throw new Error(error.error || 'Password reset failed. Please try again.');
    }

    const data = await response.json();
    console.log('✅ Password reset successful');
    return data;
  },

  /**
   * Get user profile
   */
  async getProfile() {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch(`${API_URL}/profile/`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          // Session invalid, redirect
          window.location.href = '/';
        }
        throw new Error('Failed to fetch profile');
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.warn('⏱️ Profile fetch timed out');
        throw new Error('Profile fetch timed out');
      }
      throw error;
    }
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(`${API_URL}/check-auth/`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      // Log timeout errors for debugging
      if (error.name === 'AbortError') {
        console.warn('⏱️ Auth check timed out');
      }
      return false;
    }
  },
};

export { SecurityValidator };